import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "@core/theme/tokens";
import { ApiError } from "@core/http";
import { useSession } from "@core/session/SessionProvider";
import { changeMandatoryPassword } from "@portal/access/api";
import { AccessLayout } from "@portal/access/AccessLayout";
import { BigButton, Notice, PasswordField } from "@portal/access/primitives";

// SPEC-003 §3.4: cambio obligatorio de contraseña, ya autenticado
// (RequirePasswordChange trajo acá porque mustChangePassword era true).
//
// Reusa AccessLayout pese a vivir fuera de `portal/access`: la corrida
// original (2026-08-20) evitó ese layout a propósito por vivir "dentro del
// shell", y en su lugar copió el patrón de título de RoutePlaceholder — un
// componente de scaffolding, no una referencia de diseño real. El resultado
// fue una pantalla sin tarjeta ni centrado, arrinconada en la esquina
// superior izquierda (hallazgo del usuario probando en desarrollo). Esta
// pantalla ocurre antes de que el usuario pueda ver el shell (sin sidebar,
// sin header) igual que el resto del flujo de acceso, así que el mismo
// layout centrado que usa NewPasswordScreen.tsx es la referencia correcta,
// no una excepción.
//
// Mismo criterio de UI que NewPasswordScreen.tsx (SPEC-221 §4 por analogía,
// SPEC-003 §3.4 no especifica la pantalla en detalle): un solo campo visible,
// con la ayuda de longitud mínima mostrada antes de fallar. El mínimo (8)
// viene del schema del contrato (api/contracts/parts/00-base.yaml, `password:
// minLength 8`).
const MIN_LENGTH = 8;
const MANDATORY_PASSWORD_DESCRIPTION =
  "Debes establecer una contraseña nueva antes de continuar.";

export function ChangePasswordScreen() {
  const navigate = useNavigate();
  const { signOut } = useSession();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const ready = password.length >= MIN_LENGTH;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    setError(null);
    setSubmitting(true);
    try {
      // Un solo campo visible cubre `password` y `confirmation` con el mismo
      // valor, igual que NewPasswordScreen.tsx.
      await changeMandatoryPassword(password, password);
      // FINDING (bug real, confirmado probando en desarrollo): `updatePassword`
      // en el backend actualiza `password_updated_at` sin excepción para la
      // sesión actual, y `verifySession` rechaza cualquier token con
      // `issuedAt` anterior a esa marca — es decir, este mismo cambio
      // invalida el token con el que se acaba de autenticar la petición.
      // Intentar seguir logueado con `refetch()` pegaba un 401 real
      // (`SESSION_EXPIRED`) que quedaba mal reportado como "no se pudo
      // cambiar la contraseña" pese a que el cambio sí se había guardado.
      // Se resuelve igual que NewPasswordScreen.tsx: cerrar sesión acá y
      // loguearse de nuevo con la contraseña nueva, en vez de asumir que el
      // token sigue vivo.
      await signOut();
      navigate("/access", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          `La contraseña debe tener al menos ${String(MIN_LENGTH)} caracteres.`,
        );
      } else if (
        err instanceof ApiError &&
        err.status === 403 &&
        err.code === "PASSWORD_CHANGE_NOT_REQUIRED"
      ) {
        // Carrera entre pestañas: ya no hace falta cambiar nada.
        navigate("/", { replace: true });
        return;
      } else {
        setError("No se pudo cambiar la contraseña. Intenta de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AccessLayout sub="Cambio de contraseña obligatorio">
      <form onSubmit={(event) => void handleSubmit(event)}>
        <h1
          style={{
            font: `300 27px/1.2 ${tokens.font.display}`,
            color: tokens.text.primary,
            margin: "8px 0 12px",
          }}
        >
          Cambiar contraseña
        </h1>
        <p
          style={{
            font: `400 14px/1.6 ${tokens.font.ui}`,
            color: tokens.text.secondary,
            margin: "0 0 22px",
          }}
        >
          {MANDATORY_PASSWORD_DESCRIPTION}
        </p>
        {error ? <Notice>{error}</Notice> : null}
        <PasswordField
          label="Contraseña nueva"
          value={password}
          onChange={(value) => {
            setPassword(value);
            setError(null);
          }}
          placeholder="••••••••"
          autoComplete="new-password"
          error={tooShort}
        />
        <div
          style={{
            font: `500 12px ${tokens.font.ui}`,
            color: tooShort ? tokens.danger.base : tokens.text.tertiary,
            margin: "-6px 0 20px",
          }}
        >
          Mínimo {MIN_LENGTH} caracteres.
        </div>
        <BigButton type="submit" disabled={!ready || submitting}>
          {submitting ? "Guardando…" : "Guardar y continuar"}
        </BigButton>
      </form>
    </AccessLayout>
  );
}
